import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import type { ActionArgs, LoaderArgs } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { useFetcher, useLoaderData, useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import _ from 'lodash';
import * as React from 'react';
import { addToDesignLab } from '~/components/DesignLab/utils.server';
import { Attributes, AttributesStep } from '~/components/ProductDetails/Attributes';
import { MediaSection } from '~/components/ProductDetails/MediaSection';
import { PackItemAttributesSteps, PackItemImage, getPlainAttributes } from '~/components/ProductDetails/PackItem';
import AvailableInventory from '~/components/shared/AvailableInventory';
import CircularProgress from '~/components/shared/CircularProgress';
import { PackBadge } from '~/components/shared/Packs';
import SeeMoreDescription from '~/components/shared/SeeMoreDescription';
import { MuiRemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { ProductSlugLoaderData } from '~/routes/__contained/shop/$productSlug';
import type { ProductPricesActionData } from '~/routes/api/products.$productId.prices';
import type { FindOrCreatePartActionData, PartialPart } from '~/routes/api/products.$productIdOrSlug.part';
import { isError } from '~/routes/api/products.$productIdOrSlug.part';
import type { Pack } from '~/routes/build-pack/create';
import { initialPackPrices } from '~/routes/build-pack/create';
import type { ConfigurationType, ItemAttributes, PriceEntry, PricesBreakdownEstimates } from '~/services/api.server';
import { cartApi, scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { setNotification } from '~/services/cookies.server';
import { commitSession, getSession } from '~/services/session.server';
import {
  getAttributesId,
  getCartItemId,
  getFormattedPrice,
  isApproved,
  isCustomPackOrProduct,
  isCustomPackTemplate,
  isErrorResponse,
  isPackOrProductPOD,
  isPackProduct,
  isSupplierProduct,
  typedBoolean,
} from '~/utils/common';
import { ConfigurationTypes } from '~/utils/constants';
import * as gtm from '~/utils/gtm.client';

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  formData,
  defaultShouldRevalidate,
}) => {
  const action = formData?.get('_action')?.toString();
  if (action && ['delete', 'addToCart'].includes(action)) {
    return defaultShouldRevalidate;
  }

  currentUrl.searchParams.delete('part');
  nextUrl.searchParams.delete('part');
  return currentUrl.toString() !== nextUrl.toString();
};

export async function loader({ request, params }: LoaderArgs) {
  const url = new URL(request.url);
  const part = url.searchParams.get('part');
  const { productSlug } = params;

  const [{ user }, session] = await Promise.all([getUpdatedAuthentication(request), getSession(request)]);
  const cart = cartApi.getCart(session);

  if (!part || !productSlug) return json({ cart, items: null });

  const accountId = session.get('accountId');
  const partItems = await scApi.findOrCreateProductPart(user?.accessToken, accountId, productSlug, { hash: part });

  if (isErrorResponse(partItems)) {
    const url = new URL(request.url);
    throw redirect(url.pathname);
  }

  const items: Record<number, PartialPart> = Object.fromEntries(
    partItems.items.map((item) => [
      item.product_id,
      {
        id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        image: item.image,
        blank_image: item.blank_image,
        supplier_inventories: item.supplier_inventories ?? [],
        variant: item.variant,
      },
    ])
  );

  return json({ cart, items });
}

export async function action({ request }: ActionArgs) {
  const [{ headers = new Headers() }, session] = await Promise.all([
    getUpdatedAuthentication(request),
    getSession(request),
  ]);

  const formData = await request.formData();
  const action = formData.get('_action');

  const product = formData.has('product') ? JSON.parse(formData.get('product')!.toString()) : undefined;
  if (!product) {
    return json(null, { headers });
  }

  if (action === 'addToPackBuilder' && isPackProduct(product)) {
    const pack: Pack = {
      name: '',
      quantity: 100,
      container: product.container ?? null,
      filler: product.filler ?? null,
      products: product.items.map(({ product }) => product).filter(typedBoolean),
      prices: initialPackPrices,
      items_units_per_pack: product.items.reduce((acc, { product, units_per_pack }) => {
        acc[product!.id] = units_per_pack;
        return acc;
      }, {} as Record<number, number>),
      items_attributes: {},
      pack_template_id: -1,
    };

    session.set('packBuilder', pack);
    headers.append('Set-Cookie', await commitSession(session));

    return redirect('/build-pack/create', { headers });
  }

  const quantity = Number(formData.get('quantity')) || 1;

  if (action === 'addDesignLab') {
    await addToDesignLab(session, formData, headers, product);
    throw redirect('/shop', { headers });
  }

  if (action === 'addToCart') {
    const packAttributes = formData.has('packAttributes')
      ? JSON.parse(formData.get('packAttributes')!.toString())
      : undefined;
    if (!packAttributes) {
      return json(null, { headers });
    }

    const configuration_type =
      (formData.get('configuration_type') as ConfigurationType) || ConfigurationTypes.decorated;

    cartApi.addProduct(session, quantity, product, packAttributes, configuration_type);
    setNotification(session, 'success', `Your ${product.name} has been added to the cart!`);
    headers.append('Set-Cookie', await commitSession(session));
    const cart = cartApi.getCart(session);
    return json({ cart }, { headers });
  }

  return json(null, { headers });
}

export default function ProductDetailsPage() {
  const { product } = useRouteLoaderData('routes/__contained/shop/$productSlug') as ProductSlugLoaderData;

  return <ProductDetails key={product.id} />;
}

const initialQuantity = 100;

function ProductDetails() {
  const { product, pricesBreakdown, pricesBreakdownBlank, isAuthenticated } = useRouteLoaderData(
    'routes/__contained/shop/$productSlug'
  ) as ProductSlugLoaderData;
  const data = useLoaderData<typeof loader>();
  const location = useLocation();
  const navigate = useNavigate();

  const partFetcher = useFetcher<FindOrCreatePartActionData>();
  const pricesFetcher = useFetcher<ProductPricesActionData>();
  const actionsFetcher = useFetcher<typeof action>();

  const [selectColors, setSelectColors] = React.useState(true);
  const productNameRef = React.useRef<HTMLDivElement>(null);

  const debouncedPartSubmit = React.useMemo(() => _.debounce(partFetcher.submit, 500), [partFetcher.submit]);

  const items = React.useMemo(() => {
    if (!isError(partFetcher.data) && partFetcher.data?.items) {
      return partFetcher.data?.items;
    }

    if (data.items) {
      return data.items;
    }

    if (isPackProduct(product)) {
      const _items: Record<number, PartialPart> = {
        [product.container.id]: {
          ...product.container.default_part!,
          product_id: product.container.id,
          product_name: product.container.name,
        },
      };
      if (product.filler) {
        _items[product.filler.id] = {
          ...product.filler.default_part!,
          product_id: product.filler.id,
          product_name: product.filler.name,
        };
      }
      product.items.forEach((item) => {
        _items[item.product!.id] = {
          ...item.product!.default_part,
          product_id: item.product!.id,
          product_name: item.product!.name,
        };
      });

      return _items;
    }

    return { [product.id]: { ...product.default_part!, product_id: product.id, product_name: product.name } };
  }, [data.items, partFetcher.data, product]);

  const [selectedTab, setSelectedTab] = React.useState(0);
  const configuration_type = getConfigurationType(selectedTab);

  const partInCart = React.useMemo(() => {
    const selectedAttributesId = getAttributesId(
      Object.values(items).map(({ product_id, variant }) => ({
        product_id,
        attributes: Object.entries(variant).map(([name, value]) => ({ name, value: value.name })),
      }))
    );

    return data.cart.products
      .filter((p) => p.product.id === product.id && p.configuration_type === configuration_type)
      .flatMap((p) => p.parts.map((part) => ({ ...part, cartItem: p })))
      .find((part) => {
        const partAttributesId =
          'items' in part
            ? getAttributesId(part.items)
            : getAttributesId([{ product_id: product.id, attributes: part.attributes }]);

        return partAttributesId === selectedAttributesId;
      });
  }, [configuration_type, data.cart.products, items, product.id]);

  const images = React.useMemo(() => {
    const _items = Object.values(items);
    const _images = [] as { product: number; src: string; alt: string }[];
    if (isPackProduct(product)) {
      _images.push({
        product: product.id,
        src: product.image || product.blank_image || '',
        alt: product.name,
      });
    }

    _items.forEach((item) => {
      _images.push({
        product: item.product_id,
        src: item.image || item.blank_image || NoImageAvailable,
        alt: item.product_name,
      });
    });

    return _images;
  }, [items, product]);

  const prices = React.useMemo(() => {
    const _prices = {} as Record<number, { blank: PriceEntry; total: PriceEntry }>;
    const initialPriceEntry = { amount: 0, currency: 'USD', display: '0.00' } as PriceEntry;
    const initialPrice = { blank: initialPriceEntry, total: initialPriceEntry };

    const productPrices = pricesFetcher?.data?.prices;
    _prices[product.id] = productPrices ? { blank: productPrices.blank, total: productPrices.total } : initialPrice;

    if (isPackProduct(product)) {
      const pricesMap = Object.fromEntries(
        pricesFetcher?.data?.items?.map(({ prices, product: productId }) => [
          productId,
          { blank: prices?.blank ?? initialPriceEntry, total: prices?.total ?? initialPriceEntry },
        ]) ?? []
      );
      _prices[product.container.id] = pricesMap[product.container.id] ?? initialPrice;
      if (product.filler) {
        _prices[product.filler.id] = pricesMap[product.filler.id] ?? initialPrice;
      }
      product.items.forEach((item) => {
        _prices[item.product!.id] = pricesMap[item.product!.id] ?? initialPrice;
      });
    }

    return _prices;
  }, [product, pricesFetcher?.data]);

  const maxAvailableQty = Object.values(items).reduce(
    (qty, i) => Math.min(i.supplier_inventories?.[0]?.quantity ?? 0, qty),
    Infinity
  );
  const maxQuantity = selectedTab === 2 ? product.max_sample_quantity : maxAvailableQty || undefined;

  const getInitialQuantity = React.useCallback(() => {
    if (partInCart?.quantity) return partInCart.quantity;
    if (maxQuantity === undefined) return initialQuantity;
    return maxQuantity > initialQuantity ? initialQuantity : maxQuantity;
  }, [maxQuantity, partInCart?.quantity]);

  const [quantity, setQuantity] = React.useState<number | null>(getInitialQuantity);
  const [[selectedAttributes], setSelectedAttribues] = React.useState(() => [getSelectedAttributes(items)]);
  const [[changingItems], setChangingItems] = React.useState([new Set()]);

  React.useEffect(() => {
    gtm.push_view_item(product);
  }, [product]);

  React.useEffect(() => {
    const _quantity = getInitialQuantity();
    setQuantity(_quantity);
  }, [getInitialQuantity]);

  const partHash = isError(partFetcher.data) ? null : partFetcher.data?.partHash;
  React.useEffect(() => {
    if (partHash) {
      navigate({ search: `part=${partHash}` }, { preventScrollReset: true });
    }
    setChangingItems([new Set()]);
  }, [partHash, navigate]);

  const serializedAttributes = JSON.stringify(Array.from(selectedAttributes.values()));
  React.useEffect(() => {
    if (!quantity) return;

    const timeout = setTimeout(() => {
      pricesFetcher.submit(
        { quantity: quantity.toString(), attributes: serializedAttributes, configuration_type },
        { method: 'post', action: `/api/products/${product.id}/prices`, preventScrollReset: true }
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
    // Not sure why fetcher.submit is changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration_type, product.id, quantity, serializedAttributes]);

  const handleChangeAttribute = (
    productId: number,
    attribute: string,
    value: string,
    newAttributes?: Record<string, string>
  ) => {
    const currentAttributes = selectedAttributes.get(productId);

    if (!currentAttributes) return;

    if (newAttributes) {
      selectedAttributes.set(productId, { product: productId, attributes: newAttributes });
      setSelectedAttribues([selectedAttributes]);
    } else {
      selectedAttributes.set(productId, {
        product: productId,
        attributes: { ...currentAttributes.attributes, [attribute]: value },
      });
      setSelectedAttribues([selectedAttributes]);
    }

    changingItems.add(productId);
    setChangingItems([changingItems]);

    const attributes = JSON.stringify(Array.from(selectedAttributes.values()));
    debouncedPartSubmit(
      { attributes },
      { method: 'post', action: `/api/products/${product.id}/part`, preventScrollReset: true }
    );
  };

  const handleChangeTab = (e: React.SyntheticEvent<Element, Event>, value: number) => {
    if (value === selectedTab) return;
    setSelectedTab(value);

    if (value === 2) {
      handleChangeQuantity(1);
    } else if (selectedTab === 2) {
      handleChangeQuantity(partInCart?.quantity ?? 100);
    }
  };

  const handleChangeQuantity = (value: number | null) => {
    if (value && maxQuantity && value > maxQuantity) return;
    setQuantity(value);
  };

  const isBlank = configuration_type === ConfigurationTypes.blank;
  const prevQuantity = pricesFetcher.data?.quantity ?? quantity ?? initialQuantity;
  const isFetchingPrice = pricesFetcher.state !== 'idle' || (quantity !== null && quantity !== prevQuantity);
  const isAvailable = Object.values(items).every((i) => i.supplier_inventories?.[0]?.is_available);
  const disableActions = actionsFetcher.state !== 'idle' || !isAvailable;
  const disableAddToCart = !quantity || partInCart?.quantity === quantity || disableActions || isFetchingPrice;

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (disableAddToCart || !quantity) return;

    const clonedProduct = _.cloneDeep(product);
    clonedProduct.image = items[clonedProduct.id]?.image ?? clonedProduct.image;
    clonedProduct.prices.price = isBlank ? prices[clonedProduct.id].blank : prices[clonedProduct.id].total;

    if (isPackProduct(clonedProduct)) {
      clonedProduct.container.image = items[clonedProduct.container.id]?.image ?? clonedProduct.container.image;
      clonedProduct.container.prices.price = isBlank
        ? prices[clonedProduct.container.id].blank
        : prices[clonedProduct.container.id].total;
      if (clonedProduct.filler) {
        clonedProduct.filler.image = items[clonedProduct.filler.id]?.image ?? clonedProduct.filler.image;
        clonedProduct.filler.prices.price = isBlank
          ? prices[clonedProduct.filler.id].blank
          : prices[clonedProduct.filler.id].total;
      }
      clonedProduct.items.forEach((item) => {
        item.product!.image = items[item.product!.id]?.image ?? item.product!.image;
        item.product!.prices.price = isBlank ? prices[item.product!.id].blank : prices[item.product!.id].total;
      });
    }

    gtm.push_add_to_cart(clonedProduct, quantity, e);

    actionsFetcher.submit(
      {
        _action: 'addToCart',
        product: JSON.stringify(clonedProduct),
        packAttributes: JSON.stringify(getPackAttributes(selectedAttributes)),
        quantity: quantity.toString(),
        configuration_type: getConfigurationType(selectedTab),
      },
      { replace: true, method: 'post' }
    );
  };

  const isProductDesigned =
    Object.keys(partInCart?.cartItem.mockup_images ?? {}).length > 0 || (product.is_pod && product.design_status);
  const canBeDesigned =
    product.design_lab_ready && partInCart?.cartItem.configuration_type === ConfigurationTypes.decorated;

  const handleDesign = () => {
    if (disableAddToCart || !quantity) return;
    if (!isProductDesigned) {
      gtm.push_start_design(product);
    }

    const last_page_comes_from = `${location.pathname}${location.search}`;
    const _action = 'addDesignLab';
    if (partInCart && canBeDesigned) {
      actionsFetcher.submit(
        { _action, cartItemId: getCartItemId(partInCart.cartItem), last_page_comes_from },
        { method: 'post', action: '/cart?index' }
      );
    } else {
      const clonedProduct = _.cloneDeep(product);
      clonedProduct.image = items[clonedProduct.id]?.image ?? clonedProduct.image;
      clonedProduct.blank_image = items[clonedProduct.id]?.blank_image ?? clonedProduct.blank_image;
      if (isPackProduct(clonedProduct)) {
        clonedProduct.container.image = items[clonedProduct.container.id]?.image ?? clonedProduct.container.image;
        clonedProduct.container.blank_image =
          items[clonedProduct.container.id]?.blank_image ?? clonedProduct.container.blank_image;
        if (clonedProduct.filler) {
          clonedProduct.filler.image = items[clonedProduct.filler.id]?.image ?? clonedProduct.filler.image;
          clonedProduct.filler.blank_image =
            items[clonedProduct.filler.id]?.blank_image ?? clonedProduct.filler.blank_image;
        }
        clonedProduct.items.forEach((item) => {
          item.product!.image = items[item.product!.id]?.image ?? item.product!.image;
          item.product!.blank_image = items[item.product!.id]?.blank_image ?? item.product!.blank_image;
        });
      }

      actionsFetcher.submit(
        {
          _action,
          quantity: quantity.toString(),
          product: JSON.stringify(clonedProduct),
          items_attributes: JSON.stringify(getPackAttributes(selectedAttributes)),
          last_page_comes_from,
        },
        { method: 'post' }
      );
    }
  };

  const handleAddToPackBuilder = () => {
    actionsFetcher.submit({ _action: 'addToPackBuilder', product: JSON.stringify(product) }, { method: 'post' });
  };

  const isCustomProduct = isCustomPackOrProduct(product);
  const isProductReadyToBuy = isCustomProduct && isApproved(product.design_status);

  const productHasMoreThanOneColorAndHasSizes =
    product.available_attributes?.['colors']?.length > 1 && Boolean(product.available_attributes?.['sizes']);
  const title = !product.design_lab_ready && !product.design_status ? "This product can't be designed" : '';

  const isSafsiraProduct = product.safsira_connected_product;
  const selectedColor = selectedAttributes.get(product.id)?.attributes?.color;

  return (
    <Grid container spacing={3} pt={3}>
      <Grid xs={12} lg={5}>
        <MediaSection
          key={product.id}
          images={images}
          isLoading={(id) => changingItems.has(id)}
          isPack={isPackProduct(product)}
          isSafsiraProduct={isSafsiraProduct}
        />
      </Grid>

      <Grid xs={12} lg={7} spacing={3}>
        <Stack gap={1} mb={1} ml={{ xs: 1, sm: 3 }} sx={{ '& > *': { color: '#212121' } }}>
          <Typography variant="h5" component="h1" ref={productNameRef}>
            {product.name}
          </Typography>
          {product.description && <SeeMoreDescription description={product.description} />}
          {(productHasMoreThanOneColorAndHasSizes || isPackProduct(product)) && selectColors && selectedTab === 0 && (
            <Typography variant="subtitle1" fontSize={14} mt={2}>
              {isPackProduct(product) ? 'Select one color for each product.' : 'Select one color for the product.'}
            </Typography>
          )}
          {(productHasMoreThanOneColorAndHasSizes || isPackProduct(product)) && !selectColors && selectedTab === 0 && (
            <Typography variant="subtitle1" fontSize={14} mt={2}>
              Select the size, choose the quantity and add to cart. Repeat the process for every size you need.
            </Typography>
          )}
        </Stack>

        <Stack bgcolor="#F0F5F9" p={{ xs: 1, sm: 3 }} borderRadius={1} gap={{ xs: 1, sm: 3 }}>
          {isPackProduct(product) ? (
            <Stack gap={{ xs: 1, md: 3 }}>
              <PackItemAttributesSteps
                product={product.container}
                selectedAttributes={selectedAttributes.get(product.container.id)?.attributes}
                onAttributeChange={handleChangeAttribute}
                unitsPerPack={1}
                price={isBlank ? prices[product.container.id].blank.amount : prices[product.container.id].total.amount}
                image={
                  <PackItemImage
                    src={items[product.container.id]?.image || product.container.image || product.container.blank_image}
                    alt={product.container.name}
                    isLoading={changingItems.has(product.container.id)}
                  />
                }
                badge={<PackBadge text="PACKAGING" />}
                colorStep={selectColors}
              />
              {product.filler && (
                <PackItemAttributesSteps
                  product={product.filler}
                  selectedAttributes={selectedAttributes.get(product.filler.id)?.attributes}
                  onAttributeChange={handleChangeAttribute}
                  unitsPerPack={1}
                  price={isBlank ? prices[product.filler.id].blank.amount : prices[product.filler.id].total.amount}
                  image={
                    <PackItemImage
                      src={items[product.filler.id]?.image || product.filler.image || product.filler.blank_image}
                      alt={product.filler.name}
                      isLoading={changingItems.has(product.filler.id)}
                    />
                  }
                  badge={<PackBadge text="FILLER" />}
                  colorStep={selectColors}
                />
              )}
              {product.items.map((item) => (
                <PackItemAttributesSteps
                  key={item.id}
                  product={item.product!}
                  selectedAttributes={selectedAttributes.get(item.product!.id)?.attributes}
                  onAttributeChange={handleChangeAttribute}
                  unitsPerPack={item.units_per_pack}
                  price={isBlank ? prices[item.product!.id].blank.amount : prices[item.product!.id].total.amount}
                  image={
                    <PackItemImage
                      src={items[item.product!.id]?.image || item.product!.image || item.product!.blank_image}
                      alt={item.product!.name}
                      isLoading={changingItems.has(item.product!.id)}
                    />
                  }
                  colorStep={selectColors}
                />
              ))}
            </Stack>
          ) : (
            <Stack bgcolor="#FFF" borderRadius="12px" p={{ xs: 1, sm: 3 }}>
              {productHasMoreThanOneColorAndHasSizes && selectedTab === 0 ? (
                <AttributesStep
                  availableAttributes={product.available_attributes}
                  selection={
                    selectedAttributes.get(product.id)?.attributes ?? getPlainAttributes(product.default_part.variant)
                  }
                  onAttributeChange={(attribute, value, newAttribues) =>
                    handleChangeAttribute(product.id, attribute, value, newAttribues)
                  }
                  unavailableVariants={product.unavailable_variants}
                  colorStep={selectColors}
                  unavailableInventoryParts={product.unavailable_inventory_parts}
                />
              ) : (
                <Attributes
                  availableAttributes={product.available_attributes}
                  selection={
                    selectedAttributes.get(product.id)?.attributes ?? getPlainAttributes(product.default_part.variant)
                  }
                  onAttributeChange={(attribute, value, newAttribues) =>
                    handleChangeAttribute(product.id, attribute, value, newAttribues)
                  }
                  unavailableVariants={product.unavailable_variants}
                  unavailableInventoryParts={product.unavailable_inventory_parts}
                />
              )}
            </Stack>
          )}

          <Stack p={2} bgcolor="#FFF" borderRadius="12px">
            {!isPackProduct(product) && (
              <Tabs value={selectedTab} onChange={handleChangeTab}>
                <Tab value={0} label="CUSTOM" sx={{ width: '33.33%' }} />
                {isSupplierProduct(product) && product.sellable && !isSafsiraProduct && (
                  <Tab value={1} label="BLANK" sx={{ width: '33.33%' }} />
                )}
                {isSupplierProduct(product) && product.max_sample_quantity && !isSafsiraProduct && (
                  <Tab value={2} label="SAMPLE" sx={{ width: '33.33%' }} />
                )}
              </Tabs>
            )}
            <Stack sx={[isPackProduct(product) && { '& > div:first-of-type': { mt: 0 } }]}>
              {selectedTab === 0 && !selectColors && !isSafsiraProduct && (
                <PricesBreakdown pricesBreakdown={pricesBreakdown} />
              )}
              {!isSafsiraProduct &&
                selectedTab === 0 &&
                (isPackProduct(product) ? false : !productHasMoreThanOneColorAndHasSizes) && (
                  <PricesBreakdown pricesBreakdown={pricesBreakdown} />
                )}
              {selectedTab === 1 && <PricesBreakdown pricesBreakdown={pricesBreakdownBlank} />}

              <Stack
                my={3}
                mt={isPackProduct(product) && productHasMoreThanOneColorAndHasSizes && selectColors ? 0 : 3}
                gap={3}
              >
                <QuantityAndPrice
                  quantity={quantity}
                  maxQuantity={maxQuantity}
                  onChangeQuantity={handleChangeQuantity}
                  isFetchingPrice={isFetchingPrice}
                  packPrice={isBlank ? prices[product.id].blank.amount : prices[product.id].total.amount}
                  totalPrice={
                    (isBlank ? prices[product.id].blank.amount : prices[product.id].total.amount) * prevQuantity
                  }
                  isEstimated={!isProductReadyToBuy}
                  isAvailable={isAvailable}
                  showInventoryInfo={selectedTab !== 2}
                  disableQtyinput={!isAvailable}
                />
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  gap={1}
                  position={{ xs: 'fixed', sm: 'initial' }}
                  bottom={0}
                  left={0}
                  width="100%"
                  p={{ xs: 2, sm: 0 }}
                  boxShadow={{
                    xs: '0px 3px 3px -2px rgba(0, 0, 0, 0.20), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
                    sm: 'none',
                  }}
                  justifyContent="end"
                  sx={{ zIndex: 1, backgroundColor: 'white', '& > *': { maxWidth: { md: 190 }, flex: 1 } }}
                >
                  {isSafsiraProduct ? (
                    <Tooltip
                      title={isAuthenticated ? '' : 'Please sign in to design and complete your purchase'}
                      placement="top"
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="large"
                          LinkComponent={MuiRemixLink}
                          href={`/embedded-design-lab?id=${product.safsira_product_id}&slug=${product.slug}&color=${selectedColor}&qty=${quantity}`}
                          disabled={disableActions || isFetchingPrice || !isAuthenticated}
                          sx={{ width: '100%', textTransform: 'uppercase' }}
                        >
                          Start Designing
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      {selectedTab === 0 &&
                        selectColors &&
                        isPackProduct(product) &&
                        !isCustomPackTemplate(product) && (
                          <Button
                            size="large"
                            onClick={handleAddToPackBuilder}
                            disabled={disableActions}
                            sx={{
                              width: { xs: '100%', md: 160 },
                            }}
                          >
                            ADD MORE ITEMS
                          </Button>
                        )}
                      {selectedTab === 0 &&
                        selectColors &&
                        (productHasMoreThanOneColorAndHasSizes || isPackProduct(product)) && (
                          <Button
                            size="large"
                            variant="outlined"
                            disabled={disableActions}
                            onClick={() => {
                              setSelectColors(false);
                              productNameRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            sx={{
                              width: { xs: '100%', md: 170 },
                            }}
                          >
                            BUY NOW
                          </Button>
                        )}
                      {(!selectColors ||
                        selectedTab !== 0 ||
                        (isPackProduct(product) ? false : !productHasMoreThanOneColorAndHasSizes)) && (
                        <Button
                          variant={
                            selectedTab !== 0 || !selectColors || isPackProduct(product) ? 'contained' : 'outlined'
                          }
                          size="large"
                          disabled={disableAddToCart}
                          onClick={handleAddToCart}
                          sx={{
                            width: { xs: '100%', md: 170 },
                            gridColumnStart: { sm: '3' },
                            gridColumnEnd: { sm: '4' },
                          }}
                        >
                          {partInCart ? 'UPDATE CART' : 'ADD TO CART'}
                        </Button>
                      )}
                      {selectedTab === 0 &&
                        (!isCustomProduct || (isPackOrProductPOD(product) && isApproved(product.design_status))) &&
                        selectColors && (
                          <Tooltip arrow placement="top" title={title}>
                            <span>
                              <Button
                                variant="contained"
                                size="large"
                                disabled={disableActions || (!product.design_lab_ready && !product.design_status)}
                                onClick={handleDesign}
                                sx={{ width: { xs: '100%', md: 200 } }}
                              >
                                {isProductDesigned ? 'EDIT DESIGN' : 'ADD TO DESIGN LAB'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                    </>
                  )}
                </Stack>
              </Stack>

              {!isCustomProduct && !isSafsiraProduct && <Divider sx={{ mb: 1 }} />}
              {selectedTab === 0 && !isCustomProduct && !isSafsiraProduct && (
                <Typography variant="body2">
                  Don't have your own design? We'll design it for free! Price is listed based in 1 color logo and 1
                  location, adding more colors may affect this price.
                </Typography>
              )}
              {selectedTab === 1 && (
                <Typography variant="body2">Buy this product in blank without decoration.</Typography>
              )}
              {selectedTab === 2 && (
                <Typography variant="body2">
                  Buy up to {product.max_sample_quantity === 1 ? '1 unit ' : `${product.max_sample_quantity} units`} of
                  this product as a sample.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}

export function PricesBreakdown({ pricesBreakdown }: { pricesBreakdown: PricesBreakdownEstimates }) {
  return (
    <Stack mt={4}>
      <Typography variant="subtitle1" mb={1}>
        Price breakdown
      </Typography>
      <Grid container bgcolor="#F0F5F9" borderRadius={2} px={2} py={0.75} spacing={0}>
        {pricesBreakdown.prices.map((price, index) => (
          <Grid key={index} xs={12} sm={3} textAlign={{ xs: 'left', sm: 'center' }}>
            <Stack direction={{ xs: 'row', sm: 'column' }}>
              <Typography
                variant="subtitle2"
                mb={{
                  xs: index !== pricesBreakdown.prices.length - 1 ? 1.5 : 0,
                  sm: 1.5,
                }}
                width={{ xs: '50%', sm: '100%' }}
              >
                {price.quantity}
              </Typography>
              <Typography variant="body2" width={{ xs: '50%', sm: '100%' }}>
                {getFormattedPrice(price.price.amount / 100)}
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export function QuantityAndPrice({
  children,
  quantity,
  maxQuantity,
  onChangeQuantity,
  isFetchingPrice = false,
  isEstimated,
  packPrice,
  totalPrice,
  isAvailable,
  showInventoryInfo,
  disableQtyinput,
}: {
  children?: React.ReactNode;
  quantity: number | null;
  maxQuantity?: number | null;
  onChangeQuantity: (value: number | null) => void;
  isFetchingPrice?: boolean;
  isEstimated?: boolean;
  packPrice: number;
  totalPrice: number;
  isAvailable: boolean;
  showInventoryInfo: boolean;
  disableQtyinput: boolean;
}) {
  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const parsedValue = parseInt(value, 10);
    if (parsedValue === 0) return;
    onChangeQuantity(value ? parsedValue : null);
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
      alignItems="baseline"
    >
      <Stack gap={1} mb={{ xs: 2, sm: 0 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1">Quantity</Typography>
          <TextField
            name="quantity"
            id="outlined-basic"
            variant="outlined"
            size="small"
            value={quantity ?? ''}
            type="number"
            inputProps={{ min: 1, max: maxQuantity }}
            onChange={handleChangeQuantity}
            disabled={disableQtyinput}
            sx={{ maxWidth: 100 }}
          />
          {children}
        </Stack>
        {showInventoryInfo && <AvailableInventory isAvailable={isAvailable} maxQuantity={maxQuantity || 0} />}
      </Stack>
      <Divider sx={{ width: '100%', mb: 2, display: { xs: 'initial', sm: 'none' } }} />
      <Stack alignItems="flex-start">
        <Stack
          direction={{ xs: 'row-reverse', sm: 'row' }}
          gap={1}
          flexWrap="nowrap"
          mb={{ xs: 2, sm: 3 }}
          alignItems="center"
        >
          <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mt: 0.5 }} />
          <Typography
            variant="body1"
            mt={0.5}
            textAlign={{ xs: 'left', sm: 'right' }}
            sx={[isFetchingPrice && { color: 'text.disabled' }]}
          >
            {isEstimated ? 'Estimated' : ''} Price: {getFormattedPrice(packPrice / 100)}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'row-reverse', sm: 'row' }} gap={1} flexWrap="nowrap" alignItems="center">
          <CircularProgress show={isFetchingPrice} size={12} />
          <Typography variant="subtitle1" textAlign="right" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
            {isEstimated ? 'Estimated Total' : 'Total Price'}: {getFormattedPrice(totalPrice / 100)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

function getSelectedAttributes(items: Record<number, PartialPart>) {
  return new Map(
    Object.entries(items).map(([id, item]) => [
      +id,
      {
        product: +id,
        attributes: getPlainAttributes(item.variant),
      },
    ])
  );
}

export function getConfigurationType(tabValue: number) {
  switch (tabValue) {
    case 0:
      return ConfigurationTypes.decorated;

    case 1:
      return ConfigurationTypes.blank;

    case 2:
      return ConfigurationTypes.sample;
  }

  return ConfigurationTypes.decorated;
}

function getPackAttributes(
  selectedAttributes: Map<
    number,
    {
      product: number;
      attributes: Record<string, string>;
    }
  >
) {
  const packAttributes: ItemAttributes[] = [];
  selectedAttributes.forEach((item) => {
    const attributes = Object.entries(item.attributes).map(([key, value]) => ({ name: key, value }));
    packAttributes.push({ product_id: item.product, attributes });
  });

  return packAttributes;
}
